import React, { useState, useEffect, useRef, useCallback } from 'react';
import { FaMapMarkerAlt, FaLocationArrow, FaTimes } from 'react-icons/fa';
import { OuigooLocationPicker } from '@ouigoo/location-picker/react';

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '';

// Custom Autocomplete Input Component with styled dropdown
const PlaceAutocompleteInput = ({
  value,
  onChange,
  onPlaceSelect,
  placeholder,
  country,
  icon,
  inputClassName,
  disabled = false
}) => {
  const inputRef = useRef(null);
  const sessionTokenRef = useRef(null);
  const skipNextFetchRef = useRef(false);
  // Skip the first debounce cycle so a pre-filled value doesn't auto-open suggestions.
  const firstRunRef = useRef(true);
  const [predictions, setPredictions] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const dropdownRef = useRef(null);
  const [showLocationPicker, setShowLocationPicker] = useState(false);

  // Initialize session token
  useEffect(() => {
    if (window.google?.maps?.places) {
      sessionTokenRef.current = new window.google.maps.places.AutocompleteSessionToken();
    }
  }, []);

  // Country code mapping
  const getCountryCode = useCallback(() => {
    return country === 'Benin' ? 'bj' : country === 'Togo' ? 'tg' : '';
  }, [country]);

  // Fetch predictions
  const fetchPredictions = useCallback(async (inputValue) => {
    if (!inputValue || inputValue.length < 2 || !window.google?.maps?.places?.AutocompleteSuggestion) {
      setPredictions([]);
      setIsOpen(false);
      return;
    }

    setIsLoading(true);
    const countryCode = getCountryCode();

    try {
      const { suggestions } = await window.google.maps.places.AutocompleteSuggestion.fetchAutocompleteSuggestions({
        input: inputValue,
        ...(countryCode ? { includedRegionCodes: [countryCode] } : {}),
        sessionToken: sessionTokenRef.current,
        includedPrimaryTypes: ['geocode', 'establishment']
      });

      setIsLoading(false);
      // Exclude city-level results (locality, administrative areas, country)
      const filtered = (suggestions || []).filter((s) => {
        const types = s.placePrediction?.types || [];
        return !types.includes('locality') &&
               !types.includes('administrative_area_level_1') &&
               !types.includes('administrative_area_level_2') &&
               !types.includes('country') &&
               !types.includes('political');
      });
      setPredictions(filtered);
      setIsOpen(true);
      setActiveIndex(-1);
    } catch {
      setIsLoading(false);
      setPredictions([]);
      setIsOpen(false);
    }
  }, [getCountryCode]);

  // Debounce input changes
  useEffect(() => {
    const timer = setTimeout(() => {
      if (firstRunRef.current) {
        firstRunRef.current = false;
        return;
      }
      if (skipNextFetchRef.current) {
        skipNextFetchRef.current = false;
        return;
      }
      fetchPredictions(value);
    }, 300);
    return () => clearTimeout(timer);
  }, [value, fetchPredictions]);

  // Handle place selection
  const handleSelectPlace = useCallback(async (suggestion) => {
    setPredictions([]);
    setIsOpen(false);
    setActiveIndex(-1);

    try {
      const place = suggestion.placePrediction.toPlace();
      await place.fetchFields({ fields: ['displayName', 'formattedAddress', 'location'] });

      // Prevent an extra fetch after selecting a prediction
      skipNextFetchRef.current = true;
      const displayName =
        place.displayName ||
        suggestion.placePrediction.mainText?.text ||
        suggestion.placePrediction.text?.text ||
        place.formattedAddress;
      onChange(displayName);
      if (onPlaceSelect) {
        onPlaceSelect({
          address: place.formattedAddress,
          name: place.displayName,
          lat: place.location?.lat(),
          lng: place.location?.lng()
        });
      }
      // Create new session token for next search
      sessionTokenRef.current = new window.google.maps.places.AutocompleteSessionToken();
    } catch (err) {
      console.error('Place details error:', err);
    }
  }, [onChange, onPlaceSelect]);

  // Handle keyboard navigation
  const handleKeyDown = (e) => {
    if (!isOpen || predictions.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setActiveIndex(prev => (prev < predictions.length - 1 ? prev + 1 : 0));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setActiveIndex(prev => (prev > 0 ? prev - 1 : predictions.length - 1));
        break;
      case 'Enter':
        e.preventDefault();
        if (activeIndex >= 0 && predictions[activeIndex]) {
          handleSelectPlace(predictions[activeIndex]);
        }
        break;
      case 'Escape':
        setIsOpen(false);
        setActiveIndex(-1);
        break;
      default:
        break;
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target) &&
        inputRef.current &&
        !inputRef.current.contains(e.target)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close dropdown when disabled
  useEffect(() => {
    if (disabled) {
      setIsOpen(false);
      setPredictions([]);
    }
  }, [disabled]);

  // Reset predictions when country changes
  useEffect(() => {
    setPredictions([]);
    setIsOpen(false);
  }, [country]);

  const containerClass = `relative ${isOpen || showLocationPicker ? 'z-30' : 'z-10'}`;

  return (
    <div className={containerClass}>
      <div className="absolute left-4 top-1/2 -translate-y-1/2 w-6 h-6 flex items-center justify-center">
        {icon}
      </div>
      <input
        ref={inputRef}
        type="text"
        value={value}
        disabled={disabled}
        onChange={(e) => { if (disabled) return; onChange(e.target.value); }}
        onFocus={() => !disabled && predictions.length > 0 && setIsOpen(true)}
        onKeyDown={(e) => { if (disabled) return; handleKeyDown(e); }}
        placeholder={placeholder}
        className={`${inputClassName} ${disabled ? 'cursor-not-allowed opacity-60' : ''}`}
        autoComplete="off"
      />
      {isLoading ? (
        <div className="absolute right-4 top-1/2 -translate-y-1/2">
          <div className="w-4 h-4 border-2 border-gray-300 border-t-gray-600 rounded-full animate-spin"></div>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => !disabled && setShowLocationPicker(true)}
          className={`absolute right-4 top-1/2 -translate-y-1/2 transition-transform ${disabled ? 'cursor-not-allowed text-gray-300' : 'hover:scale-110 cursor-pointer'}`}
          title="Sélectionner sur la carte"
          disabled={disabled}
        >
          <FaLocationArrow className="w-4 h-4 text-gray-800" />
        </button>
      )}

      {/* Custom Dropdown */}
      {isOpen && predictions.length > 0 && (
        <div
          ref={dropdownRef}
          className="absolute top-full left-0 right-0 mt-1 bg-white rounded-lg shadow-lg border border-gray-200 overflow-hidden z-50 max-h-64 overflow-y-auto"
        >
          {predictions.map((suggestion, index) => (
            <button
              key={suggestion.placePrediction.placeId}
              type="button"
              onClick={() => handleSelectPlace(suggestion)}
              onMouseEnter={() => setActiveIndex(index)}
              className={`w-full px-4 py-3 text-left flex items-start gap-3 transition-colors ${
                index === activeIndex ? 'bg-gray-100' : 'hover:bg-gray-50'
              }`}
            >
              <FaMapMarkerAlt className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">
                  {suggestion.placePrediction.mainText?.text || suggestion.placePrediction.text?.text}
                </p>
                {suggestion.placePrediction.secondaryText?.text && (
                  <p className="text-xs text-gray-500 truncate">
                    {suggestion.placePrediction.secondaryText.text}
                  </p>
                )}
              </div>
            </button>
          ))}
        </div>
      )}

      {/* Location Picker Dialog */}
      {showLocationPicker && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4 pt-24">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl h-[75vh] flex flex-col overflow-hidden">
            {/* Dialog Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
              <h3 className="text-xl font-bold text-gray-900">Localisation</h3>
              <button
                onClick={() => setShowLocationPicker(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <FaTimes className="w-6 h-6" />
              </button>
            </div>

            {/* Location Picker */}
            <div className="flex-1 relative">
              <OuigooLocationPicker
                apiKey={GOOGLE_MAPS_API_KEY}
                theme="ouigoo"
                language="fr"
                centerOnUserLocation
                enableSatelliteView
                enableMyLocation
                enableSearch
                confirmButtonStyle="rectangle"
                onLocationSelect={(result) => {
                  skipNextFetchRef.current = true;
                  const displayName = result.name || result.address;
                  onChange(displayName);
                  if (onPlaceSelect) {
                    onPlaceSelect({
                      address: result.address,
                      name: result.name,
                      lat: result.location?.lat,
                      lng: result.location?.lng
                    });
                  }
                  setShowLocationPicker(false);
                }}
                onError={(err) => console.error('LocationPicker error', err)}
                style={{ width: '100%', height: '100%' }}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PlaceAutocompleteInput;
